import { setAuthTokenGetter } from "@kupikupi/api";
import { useSyncExternalStore } from "react";

/**
 * Mini-App auth-token bridge.
 *
 * `status` is the render contract: routes must treat `booting` as neither
 * signed-out nor signed-in while AuthBootstrap checks localStorage,
 * Telegram CloudStorage, and initData silent login.
 */
const KEY = "kk_auth_token";

type AuthStatus = "booting" | "signed-in" | "signed-out";

interface AuthSnapshot {
  token: string | null;
  ready: boolean;
  status: AuthStatus;
}

let bootReady = false;
const listeners = new Set<() => void>();
const BOOTING_SNAPSHOT: AuthSnapshot = { token: null, ready: false, status: "booting" };
let lastSnapshot: AuthSnapshot = BOOTING_SNAPSHOT;

function readToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(KEY);
}

function snapshot(): AuthSnapshot {
  const token = readToken();
  const next: AuthSnapshot = {
    token,
    ready: bootReady,
    status: !bootReady ? "booting" : token ? "signed-in" : "signed-out",
  };
  if (
    next.token === lastSnapshot.token &&
    next.ready === lastSnapshot.ready &&
    next.status === lastSnapshot.status
  ) {
    return lastSnapshot;
  }
  lastSnapshot = next;
  return lastSnapshot;
}

function emit(): void {
  for (const cb of listeners) cb();
}

/** Imperative live read for bootstrap and non-React API call sites. */
export function readAuthToken(): string | null {
  return readToken();
}

/**
 * Bridge the Mini-App JWT into `@kupikupi/api`'s auto-attach.
 *
 * `apiFetch` auto-attaches a JWT only for requests that don't already pass one
 * explicitly, and its default getter reads `localStorage["kupikupi-token"]`
 * (the WEB app's key). The Mini App stores its token under `kk_auth_token`
 * (`KEY` above), so without this bridge every GET that relies on auto-attach —
 * notably `useProductDetail` — went out ANONYMOUS even for a logged-in user.
 * That made the backend stamp `favorite:false` on detail, so a favorited
 * product showed an empty heart after refresh (#955). Mutations were unaffected
 * because they pass `authToken` explicitly. Call once at boot, before any query.
 */
export function registerApiAuthBridge(): void {
  setAuthTokenGetter(readToken);
}

export function markAuthReady(): void {
  bootReady = true;
  emit();
}

function subscribe(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  listeners.add(cb);
  const handler = (e: StorageEvent) => {
    if (e.key === KEY || e.key === null) cb();
  };
  window.addEventListener("storage", handler);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("storage", handler);
  };
}

export function useAuth(): AuthSnapshot {
  return useSyncExternalStore(subscribe, snapshot, () => BOOTING_SNAPSHOT);
}

export function useAuthToken(): string | null {
  return useAuth().token;
}

export function setAuthToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (token == null) {
    window.localStorage.removeItem(KEY);
  } else {
    window.localStorage.setItem(KEY, token);
  }
  bootReady = true;
  emit();
  // Fire storage event manually so same-tab subscribers update too.
  window.dispatchEvent(new StorageEvent("storage", { key: KEY, newValue: token }));
}

export function resetAuthForTests(): void {
  bootReady = false;
  lastSnapshot = BOOTING_SNAPSHOT;
  emit();
}
